// Resolved once so `SITE.url` and `SITE.defaultOgImage.url` cannot drift apart.
// `siteUrl()` is declared below and cannot be used inside the SITE literal.
const SITE_ORIGIN = (process.env.NEXT_PUBLIC_SITE_URL ?? "https://sticko.app").replace(/\/$/, "");

// Store listings without campaign parameters. `SITE.apps.*` below append utm_*
// for install attribution and must never be used as `sameAs`: a sameAs is an
// identity claim about the entity, and a campaign-tagged URL is not the
// entity's canonical address.
const ANDROID_LISTING =
  "https://play.google.com/store/apps/details?id=com.hqinfosystem.sticko.wastickerapps.stickers";
const IOS_LISTING = "https://apps.apple.com/in/app/sticko-animated-sticker-maker/id1592890261";

export const SITE = {
  name: "Sticko App",
  brand: "Sticko",
  alternateName: ["Sticko", "Sticko Stickers", "Sticko WhatsApp Stickers"],
  // Casing fixed: this string is the product name and reaches Organization
  // legalName, SoftwareApplication name, and the author/creator metadata tags.
  // "Whatsapp" is not how the trademark is written, and an entity name that
  // misspells the platform it integrates with is a weak disambiguation signal.
  legalName: "Sticko - WhatsApp Sticker Maker",
  url: SITE_ORIGIN,
  description:
    "Use the Sticko app to make your WhatsApp chats more colourful with thousands of free WhatsApp sticker packs and animated stickers for Android and iPhone.",
  // Site-wide default keywords. Google/Bing ignore the keywords meta tag, so
  // these add little ranking value — kept short, honest and non-repetitive so
  // they read as a topical summary for the minor/AI engines that still parse it
  // rather than as keyword stuffing. Pages can override via pageMetadata().
  keywords: [
    "WhatsApp stickers",
    "WhatsApp sticker packs",
    "free WhatsApp stickers",
    "animated stickers",
    "sticker packs download",
    "Android stickers",
    "iPhone stickers",
    "meme stickers",
    "emoji stickers",
    "WhatsApp sticker maker",
  ],
  cdn: "https://cdn.sticko.app",
  email: "support@sticko.app",
  locale: "en_US",
  language: "en",
  // Stable "first published" date for evergreen collection pages (home,
  // category, hashtag listings). Their dateModified tracks the ISR rebuild,
  // but datePublished must stay fixed so crawlers see a real publish date
  // instead of one that drifts to "now" on every revalidation.
  launchDate: "2024-01-01T00:00:00.000Z",
  // When Sticko itself launched: its first Google Play release, October 2020
  // (owner-confirmed 2026-10-03). This is the brand's foundingDate. It is NOT
  // launchDate above, which is the web pages' datePublished on ~25,000 listings
  // and stays put, nor publisher.foundingDate, which is HQ Infosystem's (2017).
  appLaunchDate: "2020-10",
  // E-E-A-T signals: identify the operating company so that LLMs and search
  // engines can correlate authorship and trust.
  publisher: {
    name: "HQ Infosystem",
    url: "https://www.hqinfosystem.com",
    address: {
      streetAddress: "425, Silver Stone Arcade, Beside DMart, Singanpore",
      addressLocality: "Surat",
      addressRegion: "Gujarat",
      postalCode: "395004",
      addressCountry: "IN",
    },
    email: "info@hqinfosystem.com",
    foundingDate: "2017-01-01",
    // The publisher's own store developer pages — each lists every app HQ
    // Infosystem ships, Sticko included, so they tie the company to the app
    // from the stores' side. Verified live 2026-10-03.
    sameAs: [
      "https://play.google.com/store/apps/developer?id=HQ+INFOSYSTEM",
      "https://apps.apple.com/in/developer/hq-infosystem/id1232323249",
    ],
  },
  social: {
    facebook: "https://www.facebook.com/stickoapp",
    instagram: "https://www.instagram.com/stickoapp",
    // The x.com form, not twitter.com — twitter.com/sticko_app 301s here, and a
    // `sameAs` should name the entity's final address rather than a redirect to
    // it (same rule as SITE.storeListings).
    //
    // The handle is NOT written out a second time for the Twitter card. It used
    // to be: SITE.social held the URL while lib/seo/metadata.ts hardcoded
    // "@stickoapp" in two places, which is how the site ended up claiming a
    // profile — https://x.com/stickoapp — that returns 404. `twitterHandle()`
    // below derives it from this URL so the two cannot disagree again.
    twitter: "https://x.com/sticko_app",
  },
  apps: {
    android: `${ANDROID_LISTING}&referrer=utm_source%3Dweb%26utm_medium%3Dweb%26utm_content%3Dimageclick%26utm_campaign%3Dhomepage`,
    ios: IOS_LISTING,
  },
  // Campaign-free canonical addresses of the same two apps, for `sameAs`.
  // These are the strongest disambiguation signal available to this brand:
  // "Sticko" collides with an established US craft-sticker brand and with an
  // unrelated iOS app of the same name, and a store listing is an identity
  // anchor that search and answer engines already resolve.
  storeListings: {
    android: ANDROID_LISTING,
    ios: IOS_LISTING,
  },
  // Entity copy for the Organization / SoftwareApplication nodes. Kept here
  // rather than inline in structured-data.ts so the brand's own description of
  // itself lives in one place.
  entity: {
    organizationDescription:
      "Sticko is a free WhatsApp sticker pack catalogue and sticker-maker app for Android and iPhone, published by HQ Infosystem. Packs are uploaded and published by their creators, and installed into WhatsApp through the Sticko app.",
    // Neutral on purpose. "Sticko" is also an established US craft-sticker brand
    // and the name of an unrelated iOS app, so this node has to say what THIS
    // Sticko is — without naming a third-party brand, which is a trademark
    // question rather than a default.
    disambiguatingDescription:
      "A WhatsApp sticker pack catalogue and sticker-maker mobile app, not a physical or scrapbook sticker brand.",
    applicationDescription:
      "Browse and download free WhatsApp sticker packs, or build your own from photos, and add them to WhatsApp on Android and iPhone.",
    // Topical scope. Deliberately narrow — knowsAbout is an expertise claim, and
    // a long list of loosely related terms reads as keyword stuffing.
    knowsAbout: [
      "WhatsApp stickers",
      "WhatsApp sticker packs",
      "animated WebP stickers",
      "sticker pack publishing",
      "WhatsApp third-party sticker API",
    ],
    featureList: [
      "Browse free WhatsApp sticker packs by hashtag and category",
      "Add sticker packs to WhatsApp on Android and iPhone",
      "Create custom stickers from photos",
      "Animated and static sticker packs",
    ],
  },
  themeColor: "#5b23dd",
  defaultOgImage: {
    // 1200×630 is the recommended OG card size in 2026 for both Google rich
    // results and X/Twitter summary_large_image.
    //
    // Served from public/ rather than the CDN on purpose. This used to point at
    // https://cdn.sticko.app/static-content/og/sticko-og-default.png, which
    // returned 404 — the whole /static-content/og/ prefix was missing while
    // every sibling asset under /static-content/ answered 200. That single dead
    // URL was shipping as og:image and twitter:image on every page, as
    // Organization.image sitewide, and as the Article.image fallback on all
    // seven guides, where an unreachable image can invalidate the rich result.
    // Keeping the asset in the repo means it is version-controlled, deploys
    // atomically with the code that references it, and is covered by the
    // `SITE image URLs` invariant in tests/seo-invariants.test.ts.
    // JPEG at 1200x800, not the old 1.89MB PNG. Two things were wrong with it:
    // the file was ~8x larger than a social card needs (every scraper fetch —
    // and the service worker precache — pulled 1.89MB), and the dimensions
    // declared here were 1200x630 while the actual file was 1536x1024, so the
    // og:image:width/height a crawler read did not describe the image it got.
    //
    // 3:2 is preserved rather than cropped to the ideal 1.91:1: the artwork
    // uses the full canvas, so cropping would cut either the logo and headline
    // at the top or the sticko.app bar at the bottom. Re-composing it for
    // 1.91:1 is a design change, not a build-time crop.
    //
    // JPEG rather than WebP deliberately. WebP would be ~91KB instead of
    // ~234KB, but WhatsApp link previews have been unreliable with WebP
    // og:image, and WhatsApp is this site's primary sharing surface.
    url: `${SITE_ORIGIN}/static/og/sticko-og-default.jpg`,
    width: 1200,
    height: 800,
    alt: "Sticko App — free WhatsApp sticker packs",
  },
} as const;

/**
 * Absolute URL for a site path, in ONE canonical spelling.
 *
 * The root is returned without a trailing slash ("https://sticko.app"), which
 * is what `<link rel="canonical">` and `og:url` already emit for the home page.
 * Before this, `siteUrl("/")` returned "https://sticko.app/" while the entity
 * `@id`s were built from `SITE.url` without the slash — so the home page
 * described itself with three spellings of one URL at once:
 *
 *   canonical            https://sticko.app
 *   Organization @id     https://sticko.app#organization
 *   WebPage @id / url    https://sticko.app/#webpage
 *
 * Consumers that resolve `@id`s by string equality treat those as different
 * nodes, which is exactly the ambiguity stable `@id`s exist to remove.
 */
/**
 * The `@handle` for Twitter card tags, derived from `SITE.social.twitter`.
 *
 * Deriving rather than repeating is the whole point: the previous code stored
 * the profile URL in one file and hardcoded the handle in another, they drifted,
 * and the site spent an unknown period attributing its cards to an account that
 * did not exist.
 */
export function twitterHandle(): string {
  const slug = SITE.social.twitter.replace(/\/+$/, "").split("/").pop() ?? "";
  return `@${slug}`;
}

export function siteUrl(path = "/"): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const origin = SITE.url.replace(/\/$/, "");
  return normalized === "/" ? origin : `${origin}${normalized}`;
}

/**
 * The path of page N in a paginated listing.
 *
 * Interpolating `${basePath}/page/${n}` directly is wrong for any base that
 * ends in a slash — and the home page's is exactly "/". That yields `//page/2`,
 * which is not a path at all but a *protocol-relative URL*: the browser
 * resolves it against the scheme and goes to `https://page/2`, a different
 * host entirely. The home page ships the "Next →" link on every render, so
 * this was a dead link for visitors and a crawl dead-end for every bot.
 *
 * Callers pass the listing's ROUTE path, not its canonical alias: the home
 * page canonicalises to "/" but is served as /sticker-pack/for-you, and only
 * the latter has a /page/[n] route beneath it (see CategoryRoute).
 *
 * Page 1 is always the bare basePath: it is excluded from the /page/[n] route
 * and is its own canonical.
 */
export function listingPagePath(basePath: string, page: number): string {
  if (page <= 1) return basePath;
  return `${basePath.replace(/\/+$/, "")}/page/${page}`;
}

// Search indexing + canonical-host enforcement go live on the real production
// deploy (Vercel sets VERCEL_ENV=production) or when SEARCH_INDEXING=enabled is
// set explicitly. Preview and local deploys stay blocked so Vercel preview URLs
// never accumulate duplicate-content rankings.
export function isIndexingEnabled(): boolean {
  return process.env.SEARCH_INDEXING === "enabled" || process.env.VERCEL_ENV === "production";
}

// Hosts that must never be canonical-redirected to the apex — staging, Vercel
// previews, and local dev — so they can be viewed directly even when the deploy
// reports VERCEL_ENV=production. (Indexing is handled separately by
// `isIndexingEnabled`; staging currently mirrors production robots rules on
// purpose so it can be tested against the real config.)
export function isNonCanonicalHost(host: string): boolean {
  if (!host) return true;
  return (
    host === "localhost" ||
    host.startsWith("localhost:") ||
    host.endsWith(".vercel.app") ||
    host.startsWith("staging")
  );
}
